import { Button } from "@/components/ui/button"
import { useAuth } from "@/contexts/AuthProvider"
import { useFormik } from "formik"
import * as Yup from 'yup';
import { useEffect, useState } from "react"
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MASCARA_CEP, MASCARA_DIA_MES_ANO, MASCARA_TELEFONE } from "@/utils/Constants";
import { InputMask } from "@/components/ui/input-mask";
import EnderecoService from "@/services/EnderecoService";
import { useToast } from "@/components/ui/use-toast";
import { RequestEditarPerfil } from "@/dtos/Request/RequestEditarPerfil";
import { NavLink, useNavigate } from "react-router-dom";
import { validarNomeCompleto } from "@/utils/NomeCompleto";

export function EditarPerfilPage() {
    const { usuario, editarPerfil } = useAuth()
    const [cepValidado, setCepValidado] = useState(false)
    const { toast } = useToast();
    const navigate = useNavigate();

    async function handleValidarCEP() {
        try {
            setCepValidado(false)
            const cep = formikFormEditarPerfil.values.CEP.replace(/_/g, '').replace(/-/g, '');
            const { Data: result } = await EnderecoService.buscarPorCep(cep);

            formikFormEditarPerfil.setValues(
                {
                    ...formikFormEditarPerfil.values,
                    Cidade: result.Cidade,
                    Logradouro: result.Logradouro || '',
                    Numero: result.Numero,
                    UF: result.UF,
                    Bairro: result.Bairro
                }
            )

            toast({
                variant: 'success',
                description: 'CEP Validado, preencha os outros campos',
                duration: 5000
            })
            setCepValidado(true)
        } catch (error: any) {
            toast({
                variant: 'destructive',
                description: error,
                duration: 5000
            })
            setCepValidado(false)
        }
    }

    const formikFormEditarPerfil = useFormik<RequestEditarPerfil>({
        initialValues: {
            CPF: usuario?.CPF || '',
            Nome: usuario?.Nome || '',
            NomeSocial: usuario?.NomeSocial || '',
            DataNascimento: usuario?.DataNascimento || '',
            Telefone: usuario?.Telefone || '',
            CEP: usuario?.Endereco.CEP || '',
            Logradouro: usuario?.Endereco.Logradouro || '',
            Numero: usuario?.Endereco.Numero || undefined,
            Complemento: usuario?.Endereco.Complemento || '',
            Cidade: usuario?.Endereco.Cidade || ''
        },
        validationSchema: Yup.object({
            Nome: Yup.string()
                .required('Obrigatório')
                .test('Validar Nome Completo', 'Informe o nome completo', validarNomeCompleto),
            NomeSocial: Yup.string(),
            Logradouro: Yup.string().required('Obrigatório'),
            Numero: Yup.string(),
            Complemento: Yup.string(),
            Cidade: Yup.string(),
            CEP: Yup.string()
                .required('Obrigatório')
                .test('Validar CEP', 'CEP Inválido', cepToValidate => {
                    if (cepToValidate.replace(/_/g, '').replace(/-/g, '').length !== 8) {
                        return false;
                    }

                    return true;
                }),
            Telefone: Yup.string()
                .required('Obrigatório')
                .test('Validar Telefone', 'Telefone Inválido', telefoneToValidate => {
                    if (telefoneToValidate
                        .replace(/_/g, '')
                        .replace(/\(/g, '')
                        .replace(/\)/g, '')
                        .replace(/-/g, '')
                        .replace(/\./g, '')
                        .replace(/\s/g, '')
                        .length !== 11) {
                        return false;
                    }

                    return true;
                }),
            DataNascimento: Yup.string().test('Validar Data Nascimento', 'Data Nascimento Inválida (01/01/1980)', (value) => {
                if (!value) {
                    return true
                }

                const dia = value.split('/')[0]
                const mes = value.split('/')[1]
                const ano = value.split('/')[2]

                try {
                    const dateTime = new Date(parseInt(ano), parseInt(mes) - 1, parseInt(dia))

                    if (dateTime >= new Date()) {
                        return false;
                    }
                } catch (error) {
                    return false;
                }

                return true;
            }),
        }),
        onSubmit: async (values) => {
            const dadosEditarCadastroValues = {
                ...values,
                Telefone: values.Telefone.replace(/_/g, '')
                    .replace(/\(/g, '')
                    .replace(/\)/g, '')
                    .replace(/-/g, '')
                    .replace(/\./g, '')
                    .replace(/\s/g, ''),
                Endereco: {
                    Numero: values.Numero,
                    CEP: values.CEP.replace(/_/g, '').replace(/-/g, ''),
                    Complemento: values.Complemento,
                    Cidade: values.Cidade,
                    Bairro: values.Bairro,
                    Logradouro: values.Logradouro,
                    UF: values.UF
                },
                CEP: values.CEP.replace(/_/g, '').replace(/-/g, '')
            } as RequestEditarPerfil

            await editarPerfil(dadosEditarCadastroValues)

            navigate('/perfil')

            toast({
                variant: 'success',
                description: 'Dados atualizados com sucesso',
                duration: 5000
            })


        }
    });

    useEffect(() => {
        if (formikFormEditarPerfil.values.CEP.replace(/_/g, '').replace(/-/g, '').length === 8) {
            handleValidarCEP();
        }
    }, [formikFormEditarPerfil.values.CEP])


    return (
        <div className="mx-3">
            <form onSubmit={formikFormEditarPerfil.handleSubmit} >
                <div className="flex flex-col md:flex-row gap-4 text-primary-foreground">
                    <div className="w-full md:w-6/12">
                        <div className="flex flex-col gap-2 mt-3">
                            <Label htmlFor="nome">Nome</Label>
                            <Input
                                tabIndex={1}
                                id="nome"
                                type="text"
                                placeholder="Nome"
                                required
                                {...formikFormEditarPerfil.getFieldProps('Nome')}
                            />
                        </div>
                        <div className="flex flex-col gap-2 mt-3">
                            <Label htmlFor="NomeSocial">Nome Social</Label>
                            <Input
                                tabIndex={1}
                                id="NomeSocial"
                                type="text"
                                placeholder="Nome Social"
                                {...formikFormEditarPerfil.getFieldProps('NomeSocial')}
                            />
                        </div>
                        <div className="flex flex-col gap-2 mt-3">
                            <Label htmlFor="DataNascimento">Data Nascimento</Label>
                            <InputMask
                                className="w-full"
                                name="DataNascimento"
                                mask={MASCARA_DIA_MES_ANO}
                                placeholder="DD/MM/AAAA"
                                type="text"
                                id="DataNascimento"
                                formik={formikFormEditarPerfil}
                            />
                        </div>
                        <div className="flex flex-col gap-2 mt-3">
                            <Label htmlFor="Telefone">Telefone</Label>
                            <InputMask
                                mask={MASCARA_TELEFONE}
                                placeholder="Telefone"
                                type="text"
                                id="Telefone"
                                required
                                formik={formikFormEditarPerfil}
                                name='Telefone'
                            />
                        </div>
                    </div>
                    <div className="w-full md:w-6/12">
                        <div className="flex flex-col gap-2 mt-3">
                            <Label htmlFor="nome">CEP</Label>
                            <InputMask
                                name="CEP"
                                mask={MASCARA_CEP}
                                placeholder="CEP"
                                type="text"
                                id="CEP"
                                formik={formikFormEditarPerfil}
                            />
                        </div>
                        <div className="flex flex-col gap-2 mt-3">
                            <Label htmlFor="Cidade">Cidade</Label>
                            <Input
                                disabled
                                tabIndex={1}
                                id="Cidade"
                                type="text"
                                placeholder="Cidade"
                                required
                                {...formikFormEditarPerfil.getFieldProps('Cidade')}
                            />
                        </div>
                        <div className="flex flex-col gap-2 mt-3">
                            <Label htmlFor="Logradouro">Endereço</Label>
                            <Input
                                tabIndex={1}
                                id="Logradouro"
                                type="text"
                                placeholder="Logradouro"
                                required
                                {...formikFormEditarPerfil.getFieldProps('Logradouro')}
                            />
                        </div>
                        <div className="flex flex-col gap-2 mt-3">
                            <Label htmlFor="Numero">Numero</Label>
                            <Input
                                tabIndex={1}
                                id="Numero"
                                type="number"
                                placeholder="Numero"
                                {...formikFormEditarPerfil.getFieldProps('Numero')}
                            />
                        </div>
                        <div className="flex flex-col gap-2 mt-3">
                            <Label htmlFor="Complemento">Complemento</Label>
                            <Input
                                tabIndex={1}
                                id="Complemento"
                                type="text"
                                placeholder="Complemento"
                                {...formikFormEditarPerfil.getFieldProps('Complemento')}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-3 mt-3 mb-10">
                    <NavLink to="/perfil">
                        <Button variant="destructive">Cancelar</Button>
                    </NavLink>
                    <Button
                        className="w-48"
                        disabled={!formikFormEditarPerfil.isValid || !cepValidado || formikFormEditarPerfil.isSubmitting} type="submit">
                        {
                            formikFormEditarPerfil.isSubmitting ? 'Salvando...' : 'Salvar'
                        }
                    </Button>
                </div>
            </form>
        </div>
    )
}